import React, { forwardRef, useEffect, useRef, useState } from "react";

import { combineRefs, EMPTY_ARRAY } from "@multiweb/reactUtils";
import { ForwardRefType } from "@multiweb/types/react";
import { ComboBoxComponent, ComboBoxOption, ComboBoxValues } from "../ComboBoxComponent";
import { ControlValueWrapper } from "../ControlValueWrapper";
import { EnumLoader } from "../EnumLoader";
import { EnumProvider } from "../EnumProvider";
import { FormComponentPropsWrapper } from "../FormComponentPropsWrapper";
import {
    ComboBoxControlDataAttributesProps,
    EditableControlComponentProps,
} from "../types";
import { ValidationErrorWrapper } from "../ValidationErrorWrapper";
import { LocalizedComboBoxOptionsWrapper } from "./LocalizedComboBoxOptionsWrapper";
import {
    getMultiEditOption,
    isEmpty,
    removeHidden,
    updateEmptyOptionValueIfNeeded,
    updateValueInMultiEditing,
} from "./utils";

export type ComboBoxProps = Omit<EditableControlComponentProps, "ref"> & ComboBoxControlDataAttributesProps;

export const ComboBox: ForwardRefType<HTMLSelectElement, ComboBoxProps> = forwardRef<HTMLSelectElement, ComboBoxProps>(({
    id,
    dataKey,
    enumName,
    hideValues,
    placeholderLocKey,
    onBlur,
    onChange: onChangeExternal,
    onMount,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    onMouseOver,
    onMouseDown,
    onKeyDown,
    onClick,
    triggerValueChange,
    encrypted,
    multiEditActive,
    enabledVisual,
    recommended,
    required,
    postponedRequired,
    isEditing,
    externalErrors,
    onValidityChanged,
    isInRequiredInvalidState,
}, forwardRef) => {
    const innerRef = useRef<HTMLSelectElement | null>(null);
    const ref = combineRefs(innerRef, forwardRef);

    // Příznak indikující, zda se v rámci daného comboboxu provedla změna hodnoty v rámci multieditace.
    // Příznak je nutné si držet kvůli situacím, kdy je na stejnou redux hodnotu napojených více comboboxů
    // a změna v jednom z nich by v režimu multieditace změnila hodnotu i v ostatních.
    const [valueChangedInMultiEditMode, setValueChangedInMultiEditMode] = useState<boolean>(false);

    useEffect(() => {
        onMount && onMount({
            getDisplayedValue: (rawValue) => rawValue,
            getControlValue: (rawValue) => {
                // hodnota k zobrazení musí být nemínusové číslo, mínusové hodnoty mají všechny defaultní optiony
                if (rawValue == null || Number.isNaN(rawValue) || rawValue < 0) {
                    return null;
                }
                return rawValue.toString();
            },
            convertControlValueToRawValue: (controlValue) => controlValue?.valueOf(),
        });
    }, [onMount]);

    const comboBox = (options: ReadonlyArray<ComboBoxOption>) => {
        return (
            <ControlValueWrapper dataKey={dataKey}>
                {({ rawValue, onChange }) => {
                    let value: string | undefined = rawValue?.toString();
                    let visibleOptions: ReadonlyArray<ComboBoxOption> = removeHidden(options, isEditing);
                    // režim multieditace
                    if (multiEditActive) {
                        // pokud jsme v režimu multieditace, probíha editace a hodnota comboboxu je změněna, je nutné smazat možnost multieditace z comboboxu
                        if (isEditing && valueChangedInMultiEditMode) {
                            visibleOptions = visibleOptions.filter(op => op.value !== ComboBoxValues.multiEditOptionValue);
                        }
                        // pokud jsme zrušili editaci v rámci multieditace, resetujeme přiznak `valueChangedInMultiEditMode`
                        if (!isEditing && valueChangedInMultiEditMode) {
                            setValueChangedInMultiEditMode(false);
                        }
                        // Viz `valueChangedInMultiEditMode`.
                        // Abysme zajistili, že se nezmění hodnota u dalších comboboxů, které můžou být na redux hodnotu napojeny,
                        // je nutné updatovat jenom comboboxy, u kterých se už hodnota měnila.
                        value = valueChangedInMultiEditMode ? updateValueInMultiEditing(visibleOptions, value, isEditing, required) : getMultiEditOption(options)?.value.toString();
                    } else {
                        // Pokud nejsme v režimu multieditace, hodnota comboboxu se může měnit pouze tehdy, je li prázdna.
                        value = updateEmptyOptionValueIfNeeded(value);
                    }

                    return (
                        <ValidationErrorWrapper {...{ dataKey, instantValidationFnDescriptions: EMPTY_ARRAY, explicitValidationFnDescriptions: EMPTY_ARRAY, externalErrors, isInRequiredInvalidState, onValidityChanged }}>
                            {({ invalid, validate }) => (
                                <FormComponentPropsWrapper {...{ postponedRequired, required, recommended, enabledVisual }}>
                                    {({ backgroundColor, disabled, readOnly }) => (
                                        <ComboBoxComponent
                                            name={id}
                                            isEmpty={isEmpty(visibleOptions, value, multiEditActive)}
                                            {...{
                                                id,
                                                encrypted,
                                                required,
                                                backgroundColor,
                                                disabled,
                                                readOnly,
                                                invalid,
                                                onClick,
                                                onMouseLeave,
                                                onMouseEnter,
                                                onMouseOver,
                                                onMouseDown,
                                                onKeyDown,
                                                onFocus,
                                                options: visibleOptions,
                                                ref,
                                                value,
                                            }
                                            }

                                            onChange={(e) => {
                                                onChange(e);
                                                onChangeExternal && onChangeExternal(e);
                                                validate();
                                                triggerValueChange && triggerValueChange();
                                                // Pokud jsme v rámci režimu multieditace udělali změnu, nastavíme příznak na true
                                                if (multiEditActive && e !== ComboBoxValues.multiEditOptionValue) {
                                                    setValueChangedInMultiEditMode(true);
                                                }
                                            }}
                                            onBlur={(e) => {
                                                validate();
                                                onBlur && onBlur(e);
                                            }}
                                        />
                                    )}
                                </FormComponentPropsWrapper>
                            )}
                        </ ValidationErrorWrapper>
                    );
                }
                }
            </ControlValueWrapper>
        );
    };

    return (
        <EnumLoader enumName={enumName}>
            <EnumProvider enumName={enumName}>
                { ({ enumType }) => (
                    enumType ? (
                        <LocalizedComboBoxOptionsWrapper {...{ enumType, placeholderLocKey, multiEditActive, isEditing, required, hideValues }}>
                            { ({ options }) => (
                                comboBox(options)
                            )}
                        </LocalizedComboBoxOptionsWrapper>
                    ) : comboBox(EMPTY_ARRAY)
                )}
            </EnumProvider>
        </EnumLoader>
    );
});
